import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useLazyQuery } from '@apollo/client';
import { omit } from 'lodash-es';
import { eligibilityLogger } from '../utils/logger';
import { GET_APP_CONFIG_QUERY } from '../api/getAppConfig';
import { ConfigUnified } from '../types/config';

interface ConfigContextType {
  config: ConfigUnified | undefined;
  loading: boolean;
  error: boolean;
}

const ConfigContext = createContext<ConfigContextType | undefined>(undefined);

interface ConfigProviderProps {
  children: ReactNode;
}

export const ConfigProvider: React.FC<ConfigProviderProps> = ({ children }) => {
  const [getAppConfig, { data, loading, error }] = useLazyQuery<{ getAppConfig: ConfigUnified }>(
    GET_APP_CONFIG_QUERY,
  );

  const [config, setConfig] = useState<ConfigUnified | undefined>(undefined);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    // Fetch config on mount if not already loaded
    if (!config && !loading && !data) {
      getAppConfig();
    }
  }, [getAppConfig, config, loading, data]);

  useEffect(() => {
    if (loading || !data) {
      return;
    }

    if (error) {
      eligibilityLogger.error('Error fetching app config', { error });
      setHasError(true);
      return;
    }

    const backendConfig = omit(data.getAppConfig, '__typename');
    const processedConfig = {
      ...backendConfig,
      contentSources: backendConfig.userDataSources!.map(({ dataSource }) => dataSource),
    };

    setConfig(processedConfig);
    setHasError(false);
  }, [data, loading, error]);

  const contextValue: ConfigContextType = {
    config,
    loading,
    error: hasError,
  };

  return <ConfigContext.Provider value={contextValue}>{children}</ConfigContext.Provider>;
};

export const useAppConfig = (): ConfigUnified | undefined => {
  const context = useContext(ConfigContext);

  if (context === undefined) {
    throw new Error('useAppConfig must be used within a ConfigProvider');
  }

  return context.config;
};
